/**
 * Configuracoes da empresa em foco (RNF12 / RNF15): prontidao para ligar os
 * motores e as abas Marca, Regua, Integracoes, Equipe e Notificacoes.
 *
 * Decisoes nao obvias:
 *  - SEM atualizacao automatica (`useApiResource` sem `refreshMs`). Um refetch
 *    a cada 30 s, como nas telas de numero, sobrescreveria o rascunho que a
 *    pessoa esta editando. Configuracao muda quando alguem salva, nao sozinha.
 *  - A pagina guarda a configuracao EFETIVA (`efetivo`): comeca com o que
 *    veio do servidor e cada aba devolve a fatia salva por `onSaved`. E dela
 *    -- nunca do rascunho -- que a prontidao le. O WhatsApp fica fora disso
 *    porque reconectar tem efeito imediato, sem "Salvar".
 *  - As cinco abas ficam MONTADAS e as inativas recebem `hidden`. Trocar de
 *    aba nao pode jogar fora o que foi digitado na anterior; desmontar e
 *    remontar faria exatamente isso. O painel escondido nao tem classe de
 *    `display` propria para o atributo `hidden` valer.
 *  - `key={company.id}` no bloco carregado: trocar a empresa em foco descarta
 *    todos os rascunhos e a configuracao efetiva, em vez de herdar o nome de
 *    uma empresa na outra.
 *  - A aba ativa vai para a URL (`?aba=integracoes`, `replace`): o proximo
 *    passo da prontidao, um insight da visao geral ou um link de suporte
 *    conseguem abrir a secao certa. Trocar de aba nao empilha historico.
 *  - Nao usa `usePeriod`: configuracao nao e recortada por janela de tempo.
 *    E a unica tela do grupo Empresa em que a janela do cabecalho nao muda
 *    nada, e isso e deliberado (mock.ts, decisao 2).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { EditorDeComissao } from '../../../components/EditorDeComissao';
import { ErrorPanel, SimulatedNotice } from '../../../components/PageState';
import { Badge, Footnote, HeroBanner, Tabs, type TabItem } from '../../../components/ui';
import { useApiResource } from '../../../lib/api';
import { useCompany } from '../../../lib/companyContext';
import { formatCount, formatDateTime, formatPercentFromBps } from '../../../lib/format';
import type { CompanySummary, TeamMember } from '../../../types';
import { fetchConfiguracoes } from '../api';
import { AbaEquipe } from '../components/AbaEquipe';
import { AbaIntegracoes } from '../components/AbaIntegracoes';
import { AbaMarca } from '../components/AbaMarca';
import { AbaNotificacoes } from '../components/AbaNotificacoes';
import { AbaRegua } from '../components/AbaRegua';
import { ProntidaoDosMotores } from '../components/ProntidaoDosMotores';
import { calcularProntidao, pendentesPorAba } from '../prontidao';
import type { AbaConfiguracoes, CadenceSettings, CompanySettings, IntegrationsSettings, WhatsAppIntegration } from '../types';
import { ABAS, ABA_LABEL, STATUS_EMPRESA } from '../vocabulario';

const PARAM_ABA = 'aba';

function ehAba(valor: string | null): valor is AbaConfiguracoes {
  return valor !== null && (ABAS as readonly string[]).includes(valor);
}

function painelId(aba: AbaConfiguracoes): string {
  return `painel-${aba}`;
}

function Esqueleto(): JSX.Element {
  return (
    <>
      <p role="status" className="sr-only">
        Carregando as configurações
      </p>
      <div aria-hidden="true" className="flex flex-col gap-6">
        <div className="h-40 animate-pulse rounded-2xl border border-line bg-surface" />
        <div className="flex gap-2">
          {ABAS.map((aba) => (
            <span key={aba} className="h-11 w-28 animate-pulse rounded-xl border border-line bg-surface" />
          ))}
        </div>
        <div className="h-64 animate-pulse rounded-2xl border border-line bg-surface" />
        <div className="h-40 animate-pulse rounded-2xl border border-line bg-surface" />
      </div>
    </>
  );
}

export function Configuracoes(): JSX.Element {
  const { company } = useCompany();

  useEffect(() => {
    document.title = `Configurações — ${company.name} · Converte Fácil`;
  }, [company.name]);

  const buscar = useCallback((signal: AbortSignal) => fetchConfiguracoes(company.id, signal), [company.id]);
  const { state, reload } = useApiResource(buscar);

  const status = STATUS_EMPRESA[company.status];

  return (
    <section aria-labelledby="titulo-da-pagina" className="flex flex-col gap-6">
      <HeroBanner
        icon="config"
        title="Configurações"
        badges={
          <>
            <Badge tone={status.tone} dot>
              {status.label}
            </Badge>
            <Badge tone="neutral" icon="escudo">
              Régua só depois do fato
            </Badge>
          </>
        }
        description={
          <>
            Marca, régua, integrações, equipe e avisos de <strong className="font-semibold text-ink">{company.name}</strong>. Na prévia, cada aba guarda as alterações só neste
            navegador.
          </>
        }
        aside={
          <>
            <Badge tone="info" icon="comissao">
              Comissão {formatPercentFromBps(company.feeBps)} sobre o recuperado
            </Badge>
            <Badge tone="neutral">Modelo de cobrança: a definir</Badge>
          </>
        }
      />

      {state.status === 'loading' ? <Esqueleto /> : null}

      {state.status === 'error' ? <ErrorPanel title="Não foi possível carregar as configurações" error={state.error} onRetry={reload} /> : null}

      {state.status === 'success' ? <ConfiguracoesCarregadas key={company.id} company={company} data={state.data} simulated={state.simulated} /> : null}
    </section>
  );
}

function ConfiguracoesCarregadas({ company, data, simulated }: { readonly company: CompanySummary; readonly data: CompanySettings; readonly simulated: boolean }): JSX.Element {
  const [params, setParams] = useSearchParams();
  const [aba, setAba] = useState<AbaConfiguracoes>(() => {
    const pedida = params.get(PARAM_ABA);
    return ehAba(pedida) ? pedida : 'marca';
  });
  const [efetivo, setEfetivo] = useState<CompanySettings>(data);
  const [whatsapp, setWhatsapp] = useState<WhatsAppIntegration>(data.integrations.whatsapp);
  const [foco, setFoco] = useState<string | null>(null);

  const prontidao = useMemo(() => calcularProntidao(efetivo, whatsapp), [efetivo, whatsapp]);
  const pendentes = pendentesPorAba(prontidao);

  const trocarAba = useCallback(
    (proxima: AbaConfiguracoes): void => {
      setAba(proxima);
      setParams(
        (anteriores) => {
          const proximos = new URLSearchParams(anteriores);
          proximos.set(PARAM_ABA, proxima);
          return proximos;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const ir = (proxima: AbaConfiguracoes, ancora: string): void => {
    trocarAba(proxima);
    setFoco(ancora);
  };

  // O painel da aba so fica visivel depois do commit; o rAF garante que o
  // alvo ja tem altura quando a rolagem acontece.
  useEffect(() => {
    if (foco === null) {
      return undefined;
    }
    const id = window.requestAnimationFrame(() => {
      document.getElementById(foco)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setFoco(null);
    });
    return () => window.cancelAnimationFrame(id);
  }, [foco]);

  const salvarRegua = useCallback((cadence: CadenceSettings): void => setEfetivo((e) => ({ ...e, cadence })), []);
  const salvarIntegracoes = useCallback((integrations: IntegrationsSettings): void => setEfetivo((e) => ({ ...e, integrations })), []);
  const salvarEquipe = useCallback((team: readonly TeamMember[]): void => setEfetivo((e) => ({ ...e, team })), []);

  const provedoresConectados = efetivo.integrations.webhooks.filter((w) => w.connected).map((w) => w.provider);

  const itens: readonly TabItem<AbaConfiguracoes>[] = ABAS.map((valor) => {
    const n = pendentes[valor] ?? 0;
    return {
      value: valor,
      label: ABA_LABEL[valor].label,
      icon: ABA_LABEL[valor].icon,
      count: n > 0 ? formatCount(n) : undefined,
    };
  });

  return (
    <>
      {simulated ? (
        <SimulatedNotice>Esta tela mostra uma configuração fictícia e fixa por empresa. Salvar, convidar, testar e reconectar funcionam só neste navegador.</SimulatedNotice>
      ) : null}

      <ProntidaoDosMotores prontidao={prontidao} onIr={ir} />

      {/* Pedido do dono (25/09): a comissao definida pela administracao fica SEMPRE
          visivel para a empresa, fora das abas, para nunca depender de clique. */}
      <EditorDeComissao company={company} editable={false} />

      <Tabs items={itens} value={aba} onChange={trocarAba} label="Seções das configurações" panelId={painelId(aba)} />

      <div>
        <div id={painelId('marca')} role="tabpanel" aria-label={ABA_LABEL.marca.label} hidden={aba !== 'marca'}>
          <AbaMarca base={data.brand} />
        </div>
        <div id={painelId('regua')} role="tabpanel" aria-label={ABA_LABEL.regua.label} hidden={aba !== 'regua'}>
          <AbaRegua base={data.cadence} company={company} provedoresConectados={provedoresConectados} refundPolicy={data.refundPolicy} onSaved={salvarRegua} />
        </div>
        <div id={painelId('integracoes')} role="tabpanel" aria-label={ABA_LABEL.integracoes.label} hidden={aba !== 'integracoes'}>
          <AbaIntegracoes base={data.integrations} company={company} whatsapp={whatsapp} onWhatsAppChange={setWhatsapp} onSaved={salvarIntegracoes} />
        </div>
        <div id={painelId('equipe')} role="tabpanel" aria-label={ABA_LABEL.equipe.label} hidden={aba !== 'equipe'}>
          <AbaEquipe base={data.team} company={company} onSaved={salvarEquipe} />
        </div>
        <div id={painelId('notificacoes')} role="tabpanel" aria-label={ABA_LABEL.notificacoes.label} hidden={aba !== 'notificacoes'}>
          <AbaNotificacoes base={data.notifications} refundPolicy={data.refundPolicy} />
        </div>
      </div>

      <Footnote>
        Nada desta tela vai para o servidor ainda: a rota <code className="rounded bg-ink/5 px-1 font-mono text-[11px]">/api/settings</code> entra com o backend do painel
        completo. Janela de atribuição: {data.attributionWindowDays} dias (ADR-001). Última alteração registrada: {formatDateTime(data.updatedAt)}.
      </Footnote>
    </>
  );
}
